/**
 * What the voiceprint pieces derive from their numbers: where in a recording one voice speaks,
 * the turn to play to hear a voice, a waveform cut to the bars a strip has room for, and where a
 * voiceprint came from, in words. Pure.
 */
import type { TFunction } from 'i18next'
import { voiceLabel } from '../questioning/recording-format'
import type { RecordingTurn } from '../questioning/recording-types'
import type { VoiceprintOrigin } from './voiceprint-types'

type TimedTurn = Pick<RecordingTurn, 'startMs' | 'endMs' | 'voice'>

/** One stretch of a lane, as shares of the recording's length (0..1). */
export interface LaneSpan {
  start: number
  end: number
}

/** Two stretches closer than this share of the recording read as one: the lane is 8 px high. */
const LANE_JOIN = 0.004

/**
 * Where in a recording one voice speaks: its turns as stretches of the whole length, in time
 * order, with stretches that all but touch joined. It shows who speaks when — real data that
 * helps tell the voices apart — and nothing of how anybody speaks.
 */
export function laneSpans(
  turns: readonly TimedTurn[],
  voice: number,
  durationMs: number
): LaneSpan[] {
  if (durationMs <= 0) return []
  const spans: LaneSpan[] = []
  for (const turn of turns) {
    if (turn.voice !== voice) continue
    const start = Math.max(0, turn.startMs) / durationMs
    const end = Math.min(durationMs, turn.endMs) / durationMs
    if (end <= start) continue
    const last = spans[spans.length - 1]
    if (last && start - last.end <= LANE_JOIN) last.end = Math.max(last.end, end)
    else spans.push({ start, end })
  }
  return spans
}

/** The longest turn of a voice: the one to play to hear it. Null for a voice with no turn. */
export function longestTurn<T extends TimedTurn>(turns: readonly T[], voice: number): T | null {
  let longest: T | null = null
  for (const turn of turns) {
    if (turn.voice !== voice) continue
    if (!longest || turn.endMs - turn.startMs > longest.endMs - longest.startMs) longest = turn
  }
  return longest
}

/** A level kept to 0..1; anything that is no number reads as silence. */
export const clampLevel = (level: number): number =>
  Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : 0

/** How many bars a waveform strip draws unless told otherwise. */
export const STRIP_BARS = 48

/**
 * A waveform cut to `bars` bars, each the loudest peak of its stretch (a mean would flatten the
 * speech between pauses). Fewer peaks than bars are drawn as they are.
 */
export function stripBars(peaks: readonly number[], bars: number = STRIP_BARS): number[] {
  const count = Math.max(0, Math.floor(bars))
  if (peaks.length <= count) return peaks.map(clampLevel)
  return Array.from({ length: count }, (_, bar) => {
    const from = Math.floor((bar * peaks.length) / count)
    const to = Math.max(from + 1, Math.floor(((bar + 1) * peaks.length) / count))
    return clampLevel(Math.max(...peaks.slice(from, to)))
  })
}

/** How far a strip is played, 0..1; nothing for a length not known. */
export function playedShare(positionMs: number, durationMs: number | null): number {
  if (!durationMs || durationMs <= 0) return 0
  return Math.min(1, Math.max(0, positionMs / durationMs))
}

/** Where a voiceprint came from, in words: "from a sample", "from a questioning · Voice 2". */
export function originText(origin: VoiceprintOrigin, voice: number | null, t: TFunction): string {
  const words = t(`humint.voice.mark.origin.${origin}`)
  return origin === 'questioning' && voice ? `${words} · ${voiceLabel(voice, t)}` : words
}
